import api from '@api';
import SourceKeys from '@components/charts/SourceKeys';
import { sdsPrimitiveColor } from '@staging-design-system/tokens';
import { formatLargeNumber } from '@utils';
import { useEffect, useState } from 'react';
import type { Audience } from 'types/schema';
import Styled, { DIAGRAM } from './AudienceOverlap.styled';

const palette = [
  sdsPrimitiveColor.pink['600'],
  sdsPrimitiveColor.pink['200'],
  sdsPrimitiveColor.pink['700']
];

interface Props {
  audienceIds: string[];
}

function AudienceOverlap({ audienceIds }: Props) {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const audienceIdsKey = audienceIds.join(',');

  useEffect(() => {
    if (!audienceIdsKey) {
      setLoading(false);
      return;
    }

    const ids = audienceIdsKey.split(',');

    setAudiences([]);
    setError(false);
    setLoading(true);

    api.audiences
      .getAudienceDetails(ids)
      .then((data) => {
        setAudiences(data);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [audienceIdsKey]);

  if (loading) {
    return <Styled.Skeleton />;
  }

  if (error) {
    return (
      <Styled.ErrorText>Unable to load audience overlap.</Styled.ErrorText>
    );
  }

  if (audiences.length < 3) {
    return null;
  }

  const [audienceA, audienceB, overlap] = audiences;
  const audienceMetrics = (audience: Audience, showTitle = true) => (
    <>
      {showTitle && <Styled.CircleTitle>{audience.title}</Styled.CircleTitle>}
      {audience.universe != null && (
        <Styled.CircleUniverse>
          {formatLargeNumber(audience.universe)}
        </Styled.CircleUniverse>
      )}
      {audience.percentage != null && (
        <Styled.CirclePercentage>
          ({Math.round(audience.percentage)}%)
        </Styled.CirclePercentage>
      )}
    </>
  );

  const [colorA, colorB, colorOverlap] = palette;

  const keyLabels = [
    audienceA.title ?? audienceA.id ?? 'audience-a',
    audienceB.title ?? audienceB.id ?? 'audience-b',
    'Overlap'
  ];
  const keyColors = [colorA, colorB, colorOverlap];

  return (
    <Styled.Root>
      <Styled.Header>Audience Overlap</Styled.Header>
      <Styled.Diagram>
        <Styled.VennSvg
          aria-hidden
          viewBox={`0 0 ${DIAGRAM.width} ${DIAGRAM.height}`}>
          <circle
            cx={DIAGRAM.circleACenterX}
            cy={DIAGRAM.radius}
            fill={colorA}
            r={DIAGRAM.radius}
          />
          <circle
            cx={DIAGRAM.circleBCenterX}
            cy={DIAGRAM.radius}
            fill={colorB}
            r={DIAGRAM.radius}
          />
          <path d={DIAGRAM.overlapPath} fill={colorOverlap} />
        </Styled.VennSvg>
        <Styled.AudienceContent>
          {audienceMetrics(audienceA)}
        </Styled.AudienceContent>
        <Styled.AudienceContent isSecond>
          {audienceMetrics(audienceB)}
        </Styled.AudienceContent>
        <Styled.OverlapContent>
          {audienceMetrics(overlap, false)}
        </Styled.OverlapContent>
      </Styled.Diagram>
      <Styled.Key>
        <SourceKeys labels={keyLabels} colors={keyColors} />
      </Styled.Key>
      <Styled.Caption>
        <Styled.CaptionItem>
          <Styled.Label>Data set: </Styled.Label>
          {overlap.datasets?.join(', ')}
        </Styled.CaptionItem>
      </Styled.Caption>
    </Styled.Root>
  );
}

export default AudienceOverlap;
